/**
 * Section-level resolver for the patient-profile delete permission context
 * (COLAB-342).
 *
 * The three inputs the predicate needs are all profile-independent, so this
 * runs ONCE at the list surface (or drawer) — not per row. Each row then feeds
 * the resolved context and its own `profile` to the pure predicate.
 *
 * The flag is a FOREIGN flag (`release-scribe-shared-patient-directory` belongs
 * to the workspace domain), so it crosses as a prop per ruling 36 (b) rather
 * than being read inside this package. The setting query's fetch is suppressed
 * when the flag is off — it would be discarded by the compound gate anyway.
 *
 * Mirrors `usePatientDocumentDeleteContext` (COLAB-385) — same three sources,
 * same tri-state mapping, same fail-closed rule during load.
 */

import {
  useCurrentWorkspace,
  useQuerySharedPatientDirectorySetting,
  useUser,
} from '@heidi/workspace/data';
import { useMemo } from 'react';
import type { PatientProfileDeleteContext } from '../../contracts/patient-profile-permissions';
import { resolveDirectoryGate } from '../../contracts/patient-shared-analytics';

/**
 * Resolves the permission context supplied to `canDeletePatientProfile`.
 *
 * The tri-state compound gate:
 * - Flag OFF → gate `false` → predicate returns `true` (legacy path)
 * - Flag ON + setting OFF → gate `false` → predicate returns `true`
 * - Flag ON + setting pending/unresolved → gate `undefined` → predicate `false`
 * - Flag ON + setting ON → gate `true` → manager-or-admin rule
 *
 * Returns a plain `boolean | undefined` on the gate field so a single boolean
 * cannot silently collapse unresolved into gate-off — the fail-open the
 * predicate is designed to prevent. See the predicate's own doc for the shape
 * of each branch.
 */
export function usePatientProfileDeleteContext(
  isSharedPatientDirectoryFlagEnabled: boolean
): PatientProfileDeleteContext {
  // The predicates match `currentUserId` against `managed_by_user_ref_id`,
  // the manager's `user_ref_id`. A kinde-only caller sits in a different id
  // space and denies instead.
  const { data: currentUserId } = useUser((d) => d?.user_ref_id ?? d?.kinde_user_id);
  const workspace = useCurrentWorkspace();
  const isWorkspaceAdmin = workspace?.user_role === 'ADMIN';

  const { data: settingEnabled, isPending: isSettingPending } =
    useQuerySharedPatientDirectorySetting({
      enabled: isSharedPatientDirectoryFlagEnabled,
    });

  const isSharedDirectoryEnabled = resolveDirectoryGate(
    isSharedPatientDirectoryFlagEnabled,
    settingEnabled,
    isSettingPending
  );

  // Stable identity keyed on the three primitive fields, so per-row callers
  // that memoise on the context (`enableRowSelection`, `rowContextMenu`,
  // `useEmptyPatientManagement`'s useMemo) don't recompute every render.
  return useMemo(
    () => ({ currentUserId, isWorkspaceAdmin, isSharedDirectoryEnabled }),
    [currentUserId, isWorkspaceAdmin, isSharedDirectoryEnabled]
  );
}
